import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { Button, Loader, MessageBlock } from '@getro/rombo';
import { Calendar } from 'lucide-react';
import toast from 'react-hot-toast';

import MainLayout from '../../components/layout/Main';
import DatePicker from '../../components/atoms/DatePicker';
import { MainLayoutHeader } from '../../components/atoms/mainLayoutHeader';
import download from '../../helpers/download';
import usePageHook from '../../hooks/usePageHook';
import { activeNetworkSelector, userSelector } from '../../redux/selectors';
import { resolveFindemUserId } from '../../services/tierGating';
import { buildPeopleFinderProjectRoute, canAccessPeopleFinderProject } from '../../services/peopleFinder';
import {
  useGetTalentAttractionDashboardQuery,
  useGetTalentAttractionProjectsQuery,
  useLazyGetTalentAttractionJobsQuery,
  useRemoveTalentAttractionMappingMutation,
  useUpdateTalentAttractionMappingMutation,
} from '../../services/talentAttraction';
import { DataState, OutreachJourney, ProjectsList, UnlinkedProjectsBanner } from './DashboardComponents';
import { JobLinkModal } from './JobLinkModal';
import { buildProjectAnalyticsCsv, buildProjectAnalyticsFilename } from './projectAnalyticsCsv';
import { TalentAttractionPage } from './themeStyles';
import './styles.css';

const number = new Intl.NumberFormat('en-US');
const percent = new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 1 });
const EMPTY_LIST = [];
const ALL_COMPANIES = 'all';

const localDate = (value) => {
  const dateOnly = typeof value === 'string' && value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return dateOnly ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3])) : new Date(value);
};
export const formatDate = (value) => (value ? localDate(value).toLocaleDateString() : '—');
const formatNumber = (value) => number.format(value || 0);
const formatPercent = (value) => percent.format(value || 0);
const formatApiDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
export const rangeForDays = (days) => {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - (days - 1));
  return { dateFrom: formatApiDate(start), dateTo: formatApiDate(end) };
};
const formatFreshness = (value) => {
  if (!value) return 'Updated recently';
  const elapsedMinutes = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 60000));
  if (elapsedMinutes < 1) return 'Updated just now';
  if (elapsedMinutes < 60) return `Updated ${elapsedMinutes} min ago`;
  const elapsedHours = Math.floor(elapsedMinutes / 60);
  if (elapsedHours < 24) return `Updated ${elapsedHours} hr ago`;
  return `Updated ${formatDate(value)}`;
};
const dataForCurrentArgument = (query) =>
  Object.prototype.hasOwnProperty.call(query, 'currentData') ? query.currentData : query.data;

export const TalentAttraction = () => {
  usePageHook('Outreach performance');

  const activeNetwork = useSelector(activeNetworkSelector);
  const user = useSelector(userSelector);
  const allowed = Boolean(activeNetwork?.isManager && activeNetwork?.talentAttractionAvailable);
  const [range, setRange] = useState({});
  const [rangePreset, setRangePreset] = useState('all');
  const [customPickerOpen, setCustomPickerOpen] = useState(false);
  const [linkingProject, setLinkingProject] = useState(null);
  const [query, setQuery] = useState('');
  const [companyFilter, setCompanyFilter] = useState(ALL_COMPANIES);
  const [unlinkedOnly, setUnlinkedOnly] = useState(false);
  const [contactedProjectsOnly, setContactedProjectsOnly] = useState(true);
  const [compact, setCompact] = useState(false);
  const contentRef = useRef(null);
  const customPickerRef = useRef(null);
  const queryOptions = { skip: !allowed };
  const networkQuery = { ...range, networkId: activeNetwork?.id };
  const dashboardQuery = useGetTalentAttractionDashboardQuery(networkQuery, queryOptions);
  const projectsQuery = useGetTalentAttractionProjectsQuery(networkQuery, queryOptions);
  const [getJobs] = useLazyGetTalentAttractionJobsQuery();
  const [updateMapping, updateState] = useUpdateTalentAttractionMappingMutation();
  const [removeMapping, removeState] = useRemoveTalentAttractionMappingMutation();
  const dashboard = dataForCurrentArgument(dashboardQuery);
  const projectData = dataForCurrentArgument(projectsQuery);
  const source = dashboard?.source || {};
  const hasOutreachData = Boolean(source.findemSucceededAt || source.status === 'fresh');
  const hasJobData = Boolean(source.redshiftSucceededAt || source.status === 'fresh');
  const hasPriorData = hasOutreachData || hasJobData;
  const linkedJobs = dashboard?.linkedJobs || dashboard?.topJobs || EMPTY_LIST;
  const projects = projectData?.projects || EMPTY_LIST;
  const contactedFilterActive = hasOutreachData && contactedProjectsOnly;
  const tableProjects = useMemo(
    () => (contactedFilterActive ? projects.filter((project) => Number(project.contacts) > 0) : projects),
    [contactedFilterActive, projects],
  );
  const findemUserId = resolveFindemUserId(activeNetwork, user);

  useEffect(() => {
    if (!contentRef.current || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => setCompact(entry.contentRect.width < 900));
    observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!customPickerOpen) return undefined;
    const frame = window.requestAnimationFrame(() => customPickerRef.current?.querySelector('#start-date-id')?.click());
    return () => window.cancelAnimationFrame(frame);
  }, [customPickerOpen]);

  useEffect(() => {
    if (unlinkedOnly && projectData && tableProjects.every((project) => project.job)) {
      setUnlinkedOnly(false);
    }
  }, [projectData, tableProjects, unlinkedOnly]);

  const projectRoute = (project) => {
    const hasAccess = canAccessPeopleFinderProject({
      findemUserId,
      projectUserIds: project.findemProjectUserIds,
    });
    if (!hasAccess) return null;

    return buildPeopleFinderProjectRoute({
      networkSlug: activeNetwork.slug,
      projectId: project.findemProjectId,
      projectOwnerId: project.findemProjectOwnerId,
      findemUserId,
    });
  };

  if (!allowed) return <Redirect to="/not-found" />;

  const loading = Boolean(
    dashboardQuery.isLoading ||
      projectsQuery.isLoading ||
      (dashboardQuery.isFetching && !dashboard) ||
      (projectsQuery.isFetching && !projectData),
  );
  const coreError = dashboardQuery.error || projectsQuery.error;
  const hasCurrentCoreData = Boolean(dashboard && projectData);
  const blockingError = Boolean(coreError && !hasCurrentCoreData);
  const backgroundError = Boolean(coreError && hasCurrentCoreData);
  const summary = dashboard?.summary || {};
  const isFiltered = Boolean(range.dateFrom && range.dateTo);
  const unlinkedCount = tableProjects.filter((project) => !project.job).length;
  const rangeLabel = isFiltered
    ? `launched ${formatDate(range.dateFrom)}–${formatDate(range.dateTo)}`
    : 'all launch dates';

  const retry = () => {
    dashboardQuery.refetch?.();
    projectsQuery.refetch?.();
  };

  const applyRangePreset = (preset, days) => {
    setRangePreset(preset);
    setCustomPickerOpen(false);
    setRange(days ? rangeForDays(days) : {});
  };

  const applyCustomRange = ({ startDate, endDate }) => {
    setRangePreset('custom');
    setRange({ dateFrom: startDate, dateTo: endDate });
    setCustomPickerOpen(false);
  };

  const toggleUnlinkedProjects = () => {
    setCompanyFilter(ALL_COMPANIES);
    setUnlinkedOnly((active) => !active);
  };

  const exportProjectAnalytics = () => {
    if (!projects.length) return;

    try {
      const csv = buildProjectAnalyticsCsv({ projects, linkedJobs, hasOutreachData, hasJobData });
      const filename = buildProjectAnalyticsFilename({
        networkSlug: activeNetwork.slug,
        dateFrom: range.dateFrom,
        dateTo: range.dateTo,
      });
      download(filename, 'text/csv', csv);
      const projectCount = `${formatNumber(projects.length)} ${projects.length === 1 ? 'project' : 'projects'}`;
      const dataStatus = [
        !hasOutreachData && 'Outreach results are still updating, so outreach metrics are blank.',
        !hasJobData && 'Job activity is still updating, so job metrics are blank.',
      ]
        .filter(Boolean)
        .join(' ');
      toast.success(`Downloaded analytics for ${projectCount} · ${rangeLabel}.${dataStatus ? ` ${dataStatus}` : ''}`);
    } catch (_error) {
      toast.error('We couldn’t export these project analytics. Please try again.');
    }
  };

  const searchJobs = (jobQuery) =>
    getJobs({ query: jobQuery, projectId: linkingProject.id, networkId: activeNetwork.id }, true).unwrap();

  const saveMapping = async (jobId) => {
    try {
      await updateMapping({ projectId: linkingProject.id, jobId, networkId: activeNetwork.id }).unwrap();
      toast.success('Job linked successfully.');
      setLinkingProject(null);
    } catch (error) {
      toast.error(error?.data?.error || 'We couldn’t link this job. Please try again.');
    }
  };

  const removeJobMapping = async () => {
    try {
      await removeMapping({ projectId: linkingProject.id, networkId: activeNetwork.id }).unwrap();
      toast.success('Job link removed.');
      setLinkingProject(null);
    } catch (_error) {
      toast.error('We couldn’t remove this job link. Please try again.');
    }
  };

  const renderSourceStatus = () => {
    if (!dashboard || !hasPriorData) return null;
    if (source.status === 'partial') {
      return (
        <MessageBlock title="Some data is delayed" type="warning">
          We’re showing the latest available results.
        </MessageBlock>
      );
    }
    if (['failed', 'stale', 'unavailable'].includes(source.status)) {
      return (
        <MessageBlock title="Update delayed" type="warning">
          Results were last updated {formatDate(source.lastSucceededAt)}.
        </MessageBlock>
      );
    }
    return null;
  };

  const renderFreshness = () => {
    if (!dashboard || !hasPriorData || !['fresh', 'refreshing'].includes(source.status)) return null;
    return (
      <div className="TalentAttraction__freshness" role="status">
        <span aria-hidden="true" />
        {source.status === 'refreshing' ? 'Updating…' : formatFreshness(source.lastSucceededAt)}
      </div>
    );
  };

  const noDataState = () => {
    if (source.status === 'pending' || source.status === 'refreshing') {
      return (
        <DataState title="Preparing your outreach dashboard">
          We’re importing your projects and job activity. Check back in a few minutes.
        </DataState>
      );
    }
    return (
      <DataState
        title="We couldn’t load outreach data"
        variant="error"
        action={<Button onClick={retry}>Try again</Button>}
      >
        Try again in a few minutes.
      </DataState>
    );
  };

  const allTimeEmpty = !loading && !blockingError && hasOutreachData && !isFiltered && projects.length === 0;

  return (
    <MainLayout>
      <MainLayoutHeader title="Outreach performance" />
      <TalentAttractionPage>
        <div
          className={`TalentAttraction__content${compact ? ' TalentAttraction__content--compact' : ''}`}
          ref={contentRef}
        >
          <section className="TalentAttraction__intro">
            <p>Every candidate you contacted, and what happened next on your job board.</p>
            <div className="TalentAttraction__range-and-status">
              <div
                className="TalentAttraction__range-control"
                title="Choose projects by creation date. Metrics show each selected project’s all-time results."
              >
                <button type="button" aria-pressed={rangePreset === 'all'} onClick={() => applyRangePreset('all')}>
                  All time
                </button>
                <button type="button" aria-pressed={rangePreset === '90'} onClick={() => applyRangePreset('90', 90)}>
                  Last 90 days
                </button>
                <button type="button" aria-pressed={rangePreset === '30'} onClick={() => applyRangePreset('30', 30)}>
                  Last 30 days
                </button>
                <button
                  type="button"
                  aria-pressed={rangePreset === 'custom'}
                  onClick={() => setCustomPickerOpen((open) => !open)}
                >
                  <Calendar aria-hidden="true" />
                  Custom
                </button>
                {customPickerOpen && (
                  <div className="TalentAttraction__custom-picker" ref={customPickerRef}>
                    <DatePicker
                      key={`${range.dateFrom || 'custom'}-${range.dateTo || 'range'}`}
                      startDate={range.dateFrom || ''}
                      endDate={range.dateTo || ''}
                      onSelectDates={applyCustomRange}
                    />
                  </div>
                )}
              </div>
              {renderFreshness()}
            </div>
          </section>

          {renderSourceStatus()}

          {blockingError && (
            <DataState
              title="We couldn’t load outreach data"
              variant="error"
              action={<Button onClick={retry}>Try again</Button>}
            >
              Your dashboard couldn’t be updated. Please try again.
            </DataState>
          )}

          {backgroundError && (
            <MessageBlock title="We couldn’t refresh your latest results" type="warning">
              We’re showing your previously loaded results. <Button onClick={retry}>Try again</Button>
            </MessageBlock>
          )}

          {loading && (
            <div className="TalentAttraction__loading">
              <Loader />
            </div>
          )}

          {!loading && !blockingError && dashboard && !hasPriorData && noDataState()}

          {allTimeEmpty && (
            <DataState title="No outreach projects yet">
              Projects will appear here after they’re created and synced.
            </DataState>
          )}

          {!loading && !blockingError && dashboard && hasPriorData && !allTimeEmpty && (
            <>
              <OutreachJourney
                summary={summary}
                projects={projects}
                hasOutreachData={hasOutreachData}
                hasJobData={hasJobData}
                rangeLabel={rangeLabel}
                formatNumber={formatNumber}
              />

              {!hasOutreachData && (
                <DataState title="Outreach results are still updating">
                  Job activity is available while your outreach results finish updating.
                </DataState>
              )}

              {!hasJobData && (
                <DataState title="Job activity is still updating">
                  Outreach results are available while your job-board activity finishes updating.
                </DataState>
              )}

              <UnlinkedProjectsBanner count={unlinkedCount} active={unlinkedOnly} onReview={toggleUnlinkedProjects} />
              <ProjectsList
                projects={tableProjects}
                linkedJobs={linkedJobs}
                query={query}
                companyFilter={companyFilter}
                unlinkedOnly={unlinkedOnly}
                contactedProjectsOnly={contactedFilterActive}
                hasHiddenProjects={contactedFilterActive && projects.length > tableProjects.length}
                hasOutreachData={hasOutreachData}
                hasJobData={hasJobData}
                networkSlug={activeNetwork.slug}
                projectRoute={projectRoute}
                onQueryChange={setQuery}
                onCompanyChange={setCompanyFilter}
                onExportProjects={exportProjectAnalytics}
                onLinkProject={setLinkingProject}
                onContactedProjectsOnlyChange={setContactedProjectsOnly}
                exportCount={projects.length}
                exportRangeLabel={rangeLabel}
                formatDate={formatDate}
                formatNumber={formatNumber}
                formatPercent={formatPercent}
              />
            </>
          )}
        </div>
      </TalentAttractionPage>

      {linkingProject && (
        <JobLinkModal
          project={linkingProject}
          searchJobs={searchJobs}
          onCancel={() => setLinkingProject(null)}
          onSave={saveMapping}
          onRemove={removeJobMapping}
          saving={updateState.isLoading}
          removing={removeState.isLoading}
        />
      )}
    </MainLayout>
  );
};
